import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { Settings, Power, AlertTriangle, Save } from 'lucide-react';

const AdminSettingsPage = () => {
    const [maintenance, setMaintenance] = useState({ enabled: false, message: 'Site is under maintenance' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/settings/maintenance');
            if (res.data.maintenanceMode) setMaintenance(res.data.maintenanceMode);
        } catch (error) {
            console.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const toggleMaintenance = async () => {
        try {
            setSaving(true);
            const newState = { ...maintenance, enabled: !maintenance.enabled };
            await api.put('/admin/settings/maintenance', newState);
            setMaintenance(newState);
            toast.success(newState.enabled ? 'Maintenance mode enabled' : 'Site is now live');
        } catch (error) {
            toast.error('Failed to update');
        } finally {
            setSaving(false);
        }
    };

    const saveMessage = async () => {
        try {
            setSaving(true);
            await api.put('/admin/settings/maintenance', maintenance);
            toast.success('Message saved');
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-slate-900"><AdminNavbar /><div className="text-center py-12 text-gray-400">Loading...</div></div>;

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Settings className="text-gray-400" /> General Settings</h1>
                        <p className="text-gray-400 text-sm">Site-wide configuration</p>
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-4xl">
                {/* Maintenance Mode */}
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-lg ${maintenance.enabled ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                                <Power className={maintenance.enabled ? 'text-red-400' : 'text-green-400'} size={24} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">Maintenance Mode</h2>
                                <p className="text-gray-400 text-sm">
                                    Status: <span className={maintenance.enabled ? 'text-red-400' : 'text-green-400'}>{maintenance.enabled ? 'ENABLED' : 'DISABLED'}</span>
                                </p>
                            </div>
                        </div>
                        <button onClick={toggleMaintenance} disabled={saving} className={`px-6 py-2 rounded-lg font-medium transition ${maintenance.enabled ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-red-600 hover:bg-red-500 text-white'}`}>
                            {maintenance.enabled ? 'Go Live' : 'Enable Maintenance'}
                        </button>
                    </div>

                    {maintenance.enabled && (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                            <div className="flex items-center gap-2 text-red-400">
                                <AlertTriangle size={18} />
                                <span className="font-medium">Site is currently offline for users</span>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-gray-400 text-sm">Maintenance Message</label>
                        <textarea value={maintenance.message} onChange={e => setMaintenance({ ...maintenance, message: e.target.value })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-24" placeholder="Message shown to users during maintenance" />
                        <button onClick={saveMessage} disabled={saving} className="mt-2 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
                            <Save size={16} /> Save Message
                        </button>
                    </div>
                </div>

                {/* Quick Links */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                    <a href="/admin/settings/fees" className="bg-slate-800 rounded-xl border border-slate-700 p-6 hover:bg-slate-700/50 transition">
                        <h3 className="text-white font-medium">Fee Settings</h3>
                        <p className="text-gray-400 text-sm">Manage trading and withdrawal fees</p>
                    </a>
                    <a href="/admin/settings/security" className="bg-slate-800 rounded-xl border border-slate-700 p-6 hover:bg-slate-700/50 transition">
                        <h3 className="text-white font-medium">Security Settings</h3>
                        <p className="text-gray-400 text-sm">2FA, IP management, session settings</p>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AdminSettingsPage;
